import { BaseComponent } from './BaseComponent';
import { PageMode } from './Enums';
import { IReportController } from './Interfaces';
import { PageTargetElement, SearchInfo, SearchResult } from './Models';
import { addClass, findElement, removeClass } from './Utils/DomUtils';

const ShadedClassName: string = 'trv-search-dialog-shaded-result'; //the results that are found, but not selected (highlighted)
const HighlightedClassName: string = 'trv-search-dialog-highlighted-result'; //the result that is currently selected (highlighted)

export class SearchManager extends BaseComponent {
    private controller: IReportController;
    private pageContainer: HTMLElement | null;
    private searchResults: SearchResult[] = [];
    private pendingHighlightItem: SearchResult | null = null;
    private highlightedElements: HTMLElement[] = [];
    private currentHighlightedElement: HTMLElement | null = null;
    private isActive: boolean = false;

    constructor(element: HTMLElement, controller: IReportController) {
        super();

        this.controller = controller;
        this.pageContainer = findElement(element, '.trv-page-container');

        this.controller.on('scrollPageReady', this.onPageReady.bind(this)).on('pageReady', this.onPageReady.bind(this));
    }

    createAIThread() {
        return this.controller.createAIThread();
    }

    getAIResponse(query: string): Promise<string> {
        return this.controller.getAIResponse(query);
    }

    search(searchInfo: SearchInfo) {
        this.isActive = true;

        this.clearColoredItems();
        this.searchResults = [];

        if (!searchInfo.searchToken || searchInfo.searchToken === '') {
            this.onSearchComplete([]);
        } else {
            this.controller.getSearchResults(searchInfo).then(this.onSearchComplete.bind(this));
        }
    }

    closeSearch() {
        this.isActive = false;
        this.clearColoredItems();
        this.searchResults = [];
    }

    highlightSearchItem(item: SearchResult) {
        if (!item) {
            return;
        }

        if (this.currentHighlightedElement) {
            removeClass(this.currentHighlightedElement, HighlightedClassName);
            addClass(this.currentHighlightedElement, ShadedClassName);
        }

        if (item.page === this.controller.getCurrentPageNumber()) {
            this.highlightItem(item);
        } else {
            if (this.controller.getPageMode() === PageMode.SinglePage) {
                this.clearColoredItems();
            } else {
                this.highlightItem(item);
            }
        }

        this.pendingHighlightItem = item;

        this.navigateToPage(item);
    }

    navigateToPage(item: SearchResult) {
        this.controller.navigateToPage(item.page, new PageTargetElement(item.id, 'search'));
    }

    private colorPageElements(results: SearchResult[]) {
        if (!results || results.length === 0) {
            return;
        }

        results.forEach((result) => {
            let searchElement = findElement(this.pageContainer, '[data-search-id=' + result.id + ']');
            if (searchElement) {
                addClass(searchElement, ShadedClassName);
                this.highlightedElements.push(searchElement);
            }
        });

        this.highlightItem(this.pendingHighlightItem);
    }

    private highlightItem(item: SearchResult | null) {
        if (item) {
            let newHighlighted = this.highlightedElements.find(function(element) {
                return element.dataset['searchId'] === item.id;
            });

            if (newHighlighted) {
                this.currentHighlightedElement = newHighlighted;

                removeClass(newHighlighted, ShadedClassName);
                addClass(newHighlighted, HighlightedClassName);
            }
        }
    }

    private clearColoredItems() {
        if (this.highlightedElements && this.highlightedElements.length > 0) {
            this.highlightedElements.forEach((element) => {
                removeClass(element, ShadedClassName);
            });
        }
        if (this.currentHighlightedElement) {
            removeClass(this.currentHighlightedElement, HighlightedClassName);
        }

        this.highlightedElements = [];
        this.currentHighlightedElement = null;
    }

    private onPageReady() {
        if (this.isActive) {
            this.colorPageElements(this.searchResults);
        }
    }

    private onSearchComplete(results: SearchResult[]) {
        this.searchResults = results;

        this.emit('searchComplete', results);

        this.colorPageElements(results);
    }
}
